import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { ElementTodo, SectionTodo, Todo as TodoContrat } from '../lib/contrat';
import { useRequete } from '../lib/requete';
import { date } from '../lib/format';
import type { ContexteFront } from '../lib/front';
import { Badge, ErreurAffichee, Section, Squelette, Vide } from './ui';
import { BandeauFraicheur } from './Fraicheur';
import { BoiteActions } from './Actions';

/**
 * Demande au pipeline de refaire ses listes, et attend qu'il ait fini.
 *
 * Les to-do sont bâties chaque nuit. Une équipe qui traite vingt leads dans la
 * matinée — un chef de projet affecté dans Dolibarr, un devis passé en commande
 * — retravaillerait sinon jusqu'au lendemain ce qui est déjà fait.
 *
 * On interroge l'état plutôt que d'attendre un délai fixe : une minute est une
 * moyenne, pas une garantie, et rendre la main trop tôt afficherait l'ancienne
 * liste en donnant à croire que le recalcul n'a rien changé.
 */
function useRecalcul(quandFini: () => void) {
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const fini = useRef(quandFini);
  fini.current = quandFini;

  useEffect(() => {
    if (!enCours) return;
    let vivant = true;
    const minuterie = window.setInterval(async () => {
      try {
        const r = await api.etatRecalcul();
        if (!vivant || r.en_cours) return;
        setEnCours(false);
        setMessage('Listes à jour.');
        fini.current();
      } catch {
        // Un sondage qui échoue n'est pas un recalcul qui échoue : on laisse
        // tourner, le suivant tranchera.
      }
    }, 4000);
    return () => {
      vivant = false;
      window.clearInterval(minuterie);
    };
  }, [enCours]);

  const lancer = async () => {
    setMessage(null);
    try {
      const r = await api.recalculer();
      setEnCours(true);
      setMessage(r.message);
    } catch {
      setMessage('Le recalcul n’a pas pu être lancé.');
    }
  };

  return { enCours, message, lancer };
}

/** Onglet « À faire » : la to-do du collaborateur connecté. */
export function Todo({
  contexteFront,
  teammateId,
}: {
  contexteFront: ContexteFront | undefined;
  teammateId: string | undefined;
}) {
  const { donnees, erreur, chargement, rafraichir } = useRequete<TodoContrat>(
    (signal, forcer) => api.todo(teammateId as string, signal, forcer),
    [teammateId],
    Boolean(teammateId),
  );
  // Appelé avant les retours anticipés : un hook ne se déclare pas sous
  // condition, et l'écran a plusieurs sorties au-dessus du rendu.
  const recalcul = useRecalcul(rafraichir);

  if (!teammateId) {
    return <Vide>Front n’a pas communiqué votre identifiant de collaborateur.</Vide>;
  }
  if (chargement) return <Squelette lignes={6} />;
  if (erreur) return <ErreurAffichee erreur={erreur} onReessayer={rafraichir} />;
  if (!donnees) return <Vide>Rien à afficher.</Vide>;

  const total = donnees.sections.reduce((somme, section) => somme + section.elements.length, 0);

  return (
    <div className="pile">
      <BandeauFraicheur fraicheur={donnees.fraicheur} />

      <header className="entete">
        <div className="entete-tete">
          <h1>{donnees.nom}</h1>
          <div className="entete-boutons">
            <button type="button" className="bouton bouton--discret" onClick={rafraichir}>
              Recharger
            </button>
            {/*
              « Recharger » relit ce que le pipeline a produit ; « Recalculer »
              lui demande de le refaire. Deux gestes différents, et confondre
              les deux ferait croire que rien ne bouge après une matinée de
              travail.
            */}
            <button
              type="button"
              className="bouton"
              onClick={recalcul.lancer}
              disabled={recalcul.enCours}
              title="Reprend Dolibarr et vos décisions. Une minute environ."
            >
              {recalcul.enCours ? 'Recalcul…' : 'Recalculer'}
            </button>
          </div>
        </div>
        {recalcul.message && <p className="aide">{recalcul.message}</p>}
        <div className="entete-meta">
          <span>To-do du {date(donnees.date)}</span>
          <span>
            {total} élément{total > 1 ? 's' : ''}
          </span>
        </div>
      </header>

      {total === 0 && <Vide>Rien à traiter aujourd’hui.</Vide>}

      {/* Les sections sont ouvertes : on itère, sans jamais coder en dur les
          clés. De nouvelles sections apparaîtront côté pipeline. */}
      {donnees.sections.map((section) => (
        <SectionListe
          key={section.cle}
          section={section}
          contexteFront={contexteFront}
          nom={donnees.nom}
          onDemande={rafraichir}
        />
      ))}
    </div>
  );
}

function SectionListe({
  section,
  contexteFront,
  nom,
  onDemande,
}: {
  section: SectionTodo;
  contexteFront: ContexteFront | undefined;
  nom: string;
  onDemande: () => void;
}) {
  return (
    <Section
      titre={section.titre}
      aide={section.aide}
      repliable
      cle={`todo.${section.cle}`}
      /* Une section vide s'ouvre repliée : elle ne demande rien, et le compteur
         de l'en-tête suffit à dire qu'il n'y a rien. */
      ouvertParDefaut={section.elements.length > 0}
      action={<Badge ton={section.elements.length > 0 ? 'info' : 'neutre'}>{section.elements.length}</Badge>}
    >
      {section.elements.length === 0 ? (
        <Vide>Rien dans cette section.</Vide>
      ) : (
        <ul className="elements">
          {[...section.elements]
            .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
            .map((element) => (
              <LigneElement
                key={`${element.type}-${element.ref}`}
                element={element}
                contexteFront={contexteFront}
                nom={nom}
                onDemande={onDemande}
              />
            ))}
        </ul>
      )}
    </Section>
  );
}

function LigneElement({
  element,
  contexteFront,
  nom,
  onDemande,
}: {
  element: ElementTodo;
  contexteFront: ContexteFront | undefined;
  nom: string;
  onDemande: () => void;
}) {
  return (
    <li className="element avec-actions">
      <div>
      <div className="element-tete">
        <strong>{element.titre}</strong>
        {typeof element.score === 'number' && (
          <Badge ton={element.score >= 70 ? 'critique' : element.score >= 40 ? 'attention' : 'neutre'}>
            {element.score}
          </Badge>
        )}
      </div>

      <p className="element-meta">
        {element.client && <span>{element.client}</span>}
        <span className="secondaire">
          {element.type} {element.ref}
        </span>
      </p>

      {element.motif_suggestion && <p className="element-motif">{element.motif_suggestion}</p>}

      {element.demande && (
        <p className="element-demande">
          <strong>Confié à Claude</strong> —{' '}
          {ETATS[element.demande.etat] ?? element.demande.etat}
          {element.demande.resultat ? ` · ${element.demande.resultat}` : ''}
        </p>
      )}
      </div>

      <BoiteActions
        cible={{
          conv_id: element.type === 'conversation' ? element.ref : null,
          projet_ref: element.type === 'projet' ? element.ref : null,
          tiers_id: null,
          client: element.client,
          lien_front: element.lien_front,
          lien_dolibarr: element.lien_dolibarr,
          explications: element.explications,
          conversations: (element.conversations_liees ?? []).map((f) => ({
            id: f.id,
            sujet: f.sujet,
            date: f.date,
            lien_front: f.lien_front,
          })),
        }}
        contexteFront={contexteFront}
        auteur={nom}
        onFait={onDemande}
      />

    </li>
  );
}

const ETATS: Record<string, string> = {
  en_attente: 'en attente de traitement',
  en_cours: 'en cours',
  faite: 'faite',
  echouee: 'échouée',
  annulee: 'annulée',
};


